/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import { STAKE_POOL_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';

export const CLAIM_REWARDS_DISCRIMINATOR = 3;

export function getClaimRewardsDiscriminatorBytes() {
  return getU8Encoder().encode(CLAIM_REWARDS_DISCRIMINATOR);
}

export type ClaimRewardsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountStakeAccount extends string | AccountMeta<string> = string,
  TAccountOwner extends string | AccountMeta<string> = string,
  TAccountUserRewardAccount extends string | AccountMeta<string> = string,
  TAccountRewardVault extends string | AccountMeta<string> = string,
  TAccountRewardMint extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends
    | string
    | AccountMeta<string> = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  TAccountClock extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountStakeAccount extends string
        ? WritableAccount<TAccountStakeAccount>
        : TAccountStakeAccount,
      TAccountOwner extends string
        ? ReadonlySignerAccount<TAccountOwner> &
            AccountSignerMeta<TAccountOwner>
        : TAccountOwner,
      TAccountUserRewardAccount extends string
        ? WritableAccount<TAccountUserRewardAccount>
        : TAccountUserRewardAccount,
      TAccountRewardVault extends string
        ? WritableAccount<TAccountRewardVault>
        : TAccountRewardVault,
      TAccountRewardMint extends string
        ? ReadonlyAccount<TAccountRewardMint>
        : TAccountRewardMint,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type ClaimRewardsInstructionData = { discriminator: number };

export type ClaimRewardsInstructionDataArgs = {};

export function getClaimRewardsInstructionDataEncoder(): FixedSizeEncoder<ClaimRewardsInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([['discriminator', getU8Encoder()]]),
    (value) => ({ ...value, discriminator: CLAIM_REWARDS_DISCRIMINATOR })
  );
}

export function getClaimRewardsInstructionDataDecoder(): FixedSizeDecoder<ClaimRewardsInstructionData> {
  return getStructDecoder([['discriminator', getU8Decoder()]]);
}

export function getClaimRewardsInstructionDataCodec(): FixedSizeCodec<
  ClaimRewardsInstructionDataArgs,
  ClaimRewardsInstructionData
> {
  return combineCodec(
    getClaimRewardsInstructionDataEncoder(),
    getClaimRewardsInstructionDataDecoder()
  );
}

export type ClaimRewardsInput<
  TAccountPool extends string = string,
  TAccountStakeAccount extends string = string,
  TAccountOwner extends string = string,
  TAccountUserRewardAccount extends string = string,
  TAccountRewardVault extends string = string,
  TAccountRewardMint extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** The stake pool */
  pool: Address<TAccountPool>;
  /** The user's stake account */
  stakeAccount: Address<TAccountStakeAccount>;
  /** The stake account owner */
  owner: TransactionSigner<TAccountOwner>;
  /** User's reward token account */
  userRewardAccount: Address<TAccountUserRewardAccount>;
  /** Pool's reward vault */
  rewardVault: Address<TAccountRewardVault>;
  /** The reward token mint */
  rewardMint: Address<TAccountRewardMint>;
  /** The token program */
  tokenProgram?: Address<TAccountTokenProgram>;
  /** Clock sysvar */
  clock: Address<TAccountClock>;
};

export function getClaimRewardsInstruction<
  TAccountPool extends string,
  TAccountStakeAccount extends string,
  TAccountOwner extends string,
  TAccountUserRewardAccount extends string,
  TAccountRewardVault extends string,
  TAccountRewardMint extends string,
  TAccountTokenProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address = typeof STAKE_POOL_PROGRAM_ADDRESS,
>(
  input: ClaimRewardsInput<
    TAccountPool,
    TAccountStakeAccount,
    TAccountOwner,
    TAccountUserRewardAccount,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountTokenProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress }
): ClaimRewardsInstruction<
  TProgramAddress,
  TAccountPool,
  TAccountStakeAccount,
  TAccountOwner,
  TAccountUserRewardAccount,
  TAccountRewardVault,
  TAccountRewardMint,
  TAccountTokenProgram,
  TAccountClock
> {
  // Program address.
  const programAddress = config?.programAddress ?? STAKE_POOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    pool: { value: input.pool ?? null, isWritable: true },
    stakeAccount: { value: input.stakeAccount ?? null, isWritable: true },
    owner: { value: input.owner ?? null, isWritable: false },
    userRewardAccount: {
      value: input.userRewardAccount ?? null,
      isWritable: true,
    },
    rewardVault: { value: input.rewardVault ?? null, isWritable: true },
    rewardMint: { value: input.rewardMint ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA' as Address<'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.pool),
      getAccountMeta(accounts.stakeAccount),
      getAccountMeta(accounts.owner),
      getAccountMeta(accounts.userRewardAccount),
      getAccountMeta(accounts.rewardVault),
      getAccountMeta(accounts.rewardMint),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getClaimRewardsInstructionDataEncoder().encode({}),
    programAddress,
  } as ClaimRewardsInstruction<
    TProgramAddress,
    TAccountPool,
    TAccountStakeAccount,
    TAccountOwner,
    TAccountUserRewardAccount,
    TAccountRewardVault,
    TAccountRewardMint,
    TAccountTokenProgram,
    TAccountClock
  >);
}

export type ParsedClaimRewardsInstruction<
  TProgram extends string = typeof STAKE_POOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The stake pool */
    pool: TAccountMetas[0];
    /** The user's stake account */
    stakeAccount: TAccountMetas[1];
    /** The stake account owner */
    owner: TAccountMetas[2];
    /** User's reward token account */
    userRewardAccount: TAccountMetas[3];
    /** Pool's reward vault */
    rewardVault: TAccountMetas[4];
    /** The reward token mint */
    rewardMint: TAccountMetas[5];
    /** The token program */
    tokenProgram: TAccountMetas[6];
    /** Clock sysvar */
    clock: TAccountMetas[7];
  };
  data: ClaimRewardsInstructionData;
};

export function parseClaimRewardsInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>
): ParsedClaimRewardsInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 8) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      pool: getNextAccount(),
      stakeAccount: getNextAccount(),
      owner: getNextAccount(),
      userRewardAccount: getNextAccount(),
      rewardVault: getNextAccount(),
      rewardMint: getNextAccount(),
      tokenProgram: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getClaimRewardsInstructionDataDecoder().decode(instruction.data),
  };
}
